import { memo, type MouseEvent } from "react";

import { type useCartLine } from "@shopify/hydrogen-react";
import {
  type CartLine,
  type Maybe,
  type MoneyV2,
  type SelectedOption,
} from "@shopify/hydrogen-react/storefront-api-types";

import { CartGiftLineQuantityAdjustButton } from "@cart/CartGiftLineQuantityAdjustButton";
import { CartItemQuantitySelector } from "@cart/CartItemQuantitySelector";
import { CartItemSelectedOptions } from "@cart/CartItemSelectedOptions";
import { CartProductGiftItemSelection } from "@cart/CartProductGiftItemSelection";
import { type CompareAtPriceDisplay } from "@components/Price/constants";
import { Badge } from "@react/components/Badge";
import { Heading } from "@react/components/Heading";
import { Icon } from "@react/components/Icon";
import { Image } from "@react/components/Image";
import { Link } from "@react/components/Link";
import { Price } from "@react/components/Price";
import { Text } from "@react/components/Text";
import { focusRing } from "@react/utilities";
import { tw } from "classnames";

type Props = {
  badgeText: string;
  bundleValueText: string;
  cartLineUpdating: boolean;
  cartLinkTo?: string;
  cartProductGiftItemButtonEditTextLabel?: string;
  cartProductGiftItemButtonTextLabel?: string;
  cartProductPage?: string;
  compareAtPriceDisplay?: Maybe<CompareAtPriceDisplay>;
  compareAtTotalAmount: Maybe<MoneyV2> | undefined;
  cost: ReturnType<typeof useCartLine>["cost"];
  handleOpenGiftWithPurchaseDrawer: (e: MouseEvent<HTMLElement>) => void;
  handleOpenProductVariantDrawer: (e: MouseEvent<HTMLElement>) => void;
  handlePreloadProduct: (e: MouseEvent<HTMLElement>) => void;
  isEditable: boolean;
  isQuantityEditable: boolean;
  linkAriaLabel?: string;
  merchandise: ReturnType<typeof useCartLine>["merchandise"];
  onlineStoreUrl: string | undefined;
  selectedGiftsWithPurchase: CartLine[] | undefined;
  selectedOptions: Partial<SelectedOption>[] | undefined;
  shippingNote: string | null;
};

const styles = {
  container: tw(
    "border-0",
    "border-b",
    "border-solid",
    "border-divider",
    "bg-background-primary",
    "px-6",
    "py-4",
    "w-full",
    "list-none"
  ),
  headingContainer: tw("flex", "flex-col", "gap-1"),
  contentContainer: tw("flex", "flex-wrap", "animate-fade-in"),
  imageContainer: tw("mr-4", "h-24", "w-24", "bg-background-secondary"),
  detailsContainer: tw("flex", "flex-col", "flex-1"),
  infoTrashContainer: tw("flex"),
  productTitle: tw("text-primary"),
  productOptionsContainer: tw(
    "flex",
    "flex-row",
    "items-center",
    "justify-between",
    "gap-1.5",
    "text-xs",
    "text-primary-muted"
  ),
  productOptions: tw("text-primary-muted"),
  shippingNote: tw("text-brick-700", "animate-fade-in"),
  lineItemActions: tw("flex", "flex-col", "gap-1", "ml-auto", "self-start"),
  removeButton: tw(
    "bg-transparent",
    "border-0",
    "outline-none",
    "text-char-500",
    focusRing
  ),
  trashIcon: tw("h-4", "w-4"),
  priceQuantityContainer: tw("justify-between", "flex", "mt-auto"),
  price: tw("self-end", "transition-opacity"),
  cartLinePlaceholder: tw("h-4", "w-8", "animate-pulse", "bg-placeholder"),
};

const CartItemViewBase = ({
  badgeText,
  bundleValueText,
  cartLineUpdating,
  cartLinkTo,
  cartProductGiftItemButtonEditTextLabel,
  cartProductGiftItemButtonTextLabel,
  cartProductPage,
  compareAtPriceDisplay,
  compareAtTotalAmount,
  cost,
  handleOpenGiftWithPurchaseDrawer,
  handleOpenProductVariantDrawer,
  handlePreloadProduct,
  isEditable,
  isQuantityEditable,
  linkAriaLabel,
  merchandise,
  onlineStoreUrl,
  selectedGiftsWithPurchase,
  selectedOptions,
  shippingNote,
}: Props) => {
  return (
    <li className={styles.container}>
      <div className={styles.contentContainer}>
        <div className={styles.imageContainer}>
          {onlineStoreUrl ? (
            <Link linkAriaLabel={linkAriaLabel} to={onlineStoreUrl} unstyled>
              <Image
                alt={`${cartLinkTo} ${
                  merchandise?.product?.title ?? ""
                } ${cartProductPage}`}
                height={96}
                src={merchandise?.image?.url}
                width={96}
              />
            </Link>
          ) : (
            <Image
              alt={`${cartLinkTo} ${
                merchandise?.product?.title ?? ""
              } ${cartProductPage}`}
              height={96}
              src={merchandise?.image?.url}
              width={96}
            />
          )}
        </div>
        <div className={styles.detailsContainer}>
          <div className={styles.infoTrashContainer}>
            <div className={styles.headingContainer}>
              <Heading as="h6" className={styles.productTitle} size="xs">
                {onlineStoreUrl ? (
                  <Link
                    linkAriaLabel={linkAriaLabel}
                    to={onlineStoreUrl}
                    unstyled
                  >
                    {merchandise?.product?.title}
                  </Link>
                ) : (
                  <span>{merchandise?.product?.title}</span>
                )}
              </Heading>
              <CartItemSelectedOptions selectedOptions={selectedOptions} />
              {!!shippingNote && (
                <Text as="caption" className={styles.shippingNote}>
                  {shippingNote}
                </Text>
              )}
            </div>
            <div className={styles.lineItemActions}>
              <CartGiftLineQuantityAdjustButton
                adjust="remove"
                className={styles.removeButton}
              >
                <Icon className={styles.trashIcon} name="trash" />
              </CartGiftLineQuantityAdjustButton>
              {isEditable && (
                <button
                  className={tw(
                    "underline",
                    "cursor-pointer",
                    "bg-transparent"
                  )}
                  onClick={handleOpenProductVariantDrawer}
                  onMouseEnter={handlePreloadProduct}
                >
                  <Text as="body2" size="sm">
                    {cartProductGiftItemButtonEditTextLabel}
                  </Text>
                </button>
              )}
            </div>
          </div>
          {badgeText && (
            <div>
              <Badge size="sm" type="secondary">
                {badgeText}
              </Badge>
            </div>
          )}
          <div className={styles.priceQuantityContainer}>
            <div className={styles.price}>
              {!cartLineUpdating && !!cost?.totalAmount && (
                <Price
                  as="body2"
                  bundleValueText={bundleValueText}
                  compareAtPrice={compareAtTotalAmount}
                  compareAtPriceDisplay={compareAtPriceDisplay}
                  price={cost.totalAmount}
                  showCurrencySuffix={false}
                />
              )}
              {cartLineUpdating && !!cost?.totalAmount && (
                <Price
                  as="body2"
                  bundleValueText={bundleValueText}
                  compareAtPriceDisplay={compareAtPriceDisplay}
                  price={cost.totalAmount}
                  showCurrencySuffix={false}
                />
              )}
              {cartLineUpdating && !cost?.totalAmount && (
                <div className={styles.cartLinePlaceholder}></div>
              )}
            </div>
            {isQuantityEditable && <CartItemQuantitySelector />}
          </div>
        </div>
        <CartProductGiftItemSelection
          buttonLabel={cartProductGiftItemButtonTextLabel}
          cartLineUpdating={cartLineUpdating}
          handleOpenGiftWithPurchaseDrawer={handleOpenGiftWithPurchaseDrawer}
          handlePreloadProduct={handlePreloadProduct}
          linkAriaLabel={linkAriaLabel}
          selectedGiftsWithPurchase={selectedGiftsWithPurchase}
          selectedOptions={selectedOptions}
          shippingNote={shippingNote}
        />
      </div>
    </li>
  );
};

export const CartItemView = memo(CartItemViewBase);
